'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { 
  ResponsiveContainer, LineChart, Line, BarChart, Bar, 
  XAxis, YAxis, Tooltip, CartesianGrid 
} from 'recharts';
import { colors } from './SharedUI';

const TIMEFRAMES = [
  { id: '15m', label: '15 Phút', interval: '15m', range: '60d' },
  { id: '30m', label: '30 Phút', interval: '30m', range: '60d' },
  { id: '1h',  label: '1 Giờ',  interval: '60m', range: '300d' }, 
  { id: '4h',  label: '4 Giờ',  interval: '60m', range: '400d' },  
  { id: '12h', label: '12 Giờ', interval: '60m', range: '600d' }, 
  { id: '1d',  label: '1 Ngày', interval: '1d',  range: '2y' },   
  { id: '2d',  label: '2 Ngày', interval: '1d',  range: '2y' }, 
  { id: '4d',  label: '4 Ngày', interval: '1d',  range: '3y' }, 
  { id: '5d',  label: '5 Ngày', interval: '1d',  range: '3y' }, 
];

export default function USStockVisualAnalytics({ defaultSymbol = 'SNDK' }) {
  const [inputSymbol, setInputSymbol] = useState(defaultSymbol);
  const [selectedTimeframe, setSelectedTimeframe] = useState('1h'); 

  const [activeSymbol, setActiveSymbol] = useState('');
  const [activeTimeframe, setActiveTimeframe] = useState('');
  const [chartData, setChartData] = useState([]);
  const [marketPrice, setMarketPrice] = useState(null);
  const [currency, setCurrency] = useState('USD');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const activeColors = useMemo(() => ({
    bg: colors?.bg || '#0b0e14',
    border: colors?.border || '#2b3139',
    green: colors?.green || '#089981',
    red: colors?.red || '#f23645',
    textSub: colors?.textSub || '#848e9c',
    textMain: colors?.textMain || '#ffffff'
  }), []);

  const handleFetchVisualData = async () => {
    const symbolToFetch = inputSymbol.trim().toUpperCase();
    if (!symbolToFetch) return;

    setIsLoading(true);
    setErrorMsg(null);

    const tfConfig = TIMEFRAMES.find(t => t.id === selectedTimeframe) || TIMEFRAMES[2];
    const proxyUrl = 'https://cors-anywhere.herokuapp.com/';
    const yahooUrl = `https://query1.finance.yahoo.com/v8/finance/chart/${symbolToFetch}?range=${tfConfig.range}&interval=${tfConfig.interval}`;

    try {
      const response = await fetch(`${proxyUrl}${yahooUrl}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error('Mã không tồn tại hoặc Proxy quá tải');
      
      const json = await response.json();
      const result = json?.chart?.result?.[0];
      if (!result) throw new Error('Không tìm thấy dữ liệu của mã này');

      setCurrency(result.meta?.currency || 'USD');
      setMarketPrice(result.meta?.regularMarketPrice || null);

      const timestamps = result.timestamp || [];
      const indicators = result.indicators?.quote?.[0] || {};
      const closes = indicators.close || [];
      const opens = indicators.open || [];
      const highs = indicators.high || [];
      const lows = indicators.low || [];
      const volumes = indicators.volume || [];

      const formattedBars = [];
      for (let index = 0; index < timestamps.length; index++) {
        if (closes[index] !== null && closes[index] !== undefined) {
          // 🛠️ ĐÃ VÁ LỖI 1: Nhân 1000 quy đổi giây hệ Yahoo về Miligiây chuẩn của JS Date
          const ms = timestamps[index] * 1000;
          const dateObj = new Date(ms);

          // 🛠️ ĐÃ VÁ LỖI 2: Ép bóc tách Thứ và Giờ theo đúng múi giờ gốc America/New_York của Sàn Mỹ
          const estDayString = dateObj.toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'long' });
          const estHourString = dateObj.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour12: false, hour: '2-digit' });
          
          const hourIndex = parseInt(estHourString, 10);

          formattedBars.push({
            ms: ms,
            timeLabel: dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            dateLabel: dateObj.toLocaleDateString([], { month: '2-digit', day: '2-digit' }),
            open: opens[index],
            high: highs[index],
            low: lows[index],
            close: closes[index],
            volume: volumes[index] || 0,
            estDayName: estDayString, // Trả ra chuỗi tường minh: "Monday", "Tuesday"...
            estHourIndex: hourIndex   // Trả ra số giờ chuẩn sàn Mỹ: 9, 10, 11...
          });
        }
      }

      setChartData(formattedBars);
      setActiveSymbol(symbolToFetch);
      setActiveTimeframe(selectedTimeframe);
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleFetchVisualData();
  }, []);

  // THUẬT TOÁN TÍNH TOÁN VOLUME PROFILE (KHÔNG ĐỔI)
  const volumeProfileData = useMemo(() => {
    if (chartData.length === 0) return [];
    const prices = chartData.map(b => b.close);
    const maxPrice = Math.max(...prices);
    const minPrice = Math.min(...prices);
    const totalBins = 12;
    const binSize = (maxPrice - minPrice) / totalBins;
    
    const bins = Array.from({ length: totalBins }, (_, i) => {
      const low = minPrice + (i * binSize);
      const high = low + binSize;
      return { priceLevel: ((low + high) / 2).toFixed(2), volume: 0, low, high };
    });

    chartData.forEach(bar => {
      for (let i = 0; i < totalBins; i++) {
        if (bar.close >= bins[i].low && bar.close <= bins[i].high) {
          bins[i].volume += bar.volume;
          break;
        }
      }
    });
    return bins;
  }, [chartData]);

  // =========================================================================
  // 🛠️ ĐÃ NÂNG CẤP: THUẬT TOÁN ĐỒNG BỘ HEATMAP MATRIX KHỚP KHUNG GIỜ NEW YORK
  // =========================================================================
  const heatmapMatrix = useMemo(() => {
    // Ánh xạ mảng Thứ theo ngôn ngữ chuỗi chuẩn của hàm toLocaleDateString
    const daysEn = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    const daysVn = ['Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6'];
    
    // Khung giờ giao dịch mở rộng của sàn Mỹ (Từ 8h sáng Pre-market đến 17h chiều Post-market)
    const tradingHours = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17]; 

    // Khởi tạo khung ma trận lưới trống rỗng
    const matrix = {};
    daysEn.forEach(d => {
      matrix[d] = {};
      tradingHours.forEach(h => { matrix[d][h] = 0; });
    });

    // Gom tích lũy khối lượng giao dịch
    chartData.forEach(bar => {
      if (matrix[bar.estDayName] && matrix[bar.estDayName][bar.estHourIndex] !== undefined) {
        matrix[bar.estDayName][bar.estHourIndex] += bar.volume;
      }
    });

    // Tìm điểm volume giao dịch đột biến nhất trong cụm phục vụ tỷ lệ chia sắc độ màu
    let maxVolumeInGrid = 1;
    daysEn.forEach(d => {
      tradingHours.forEach(h => {
        if (matrix[d][h] > maxVolumeInGrid) maxVolumeInGrid = matrix[d][h];
      });
    });

    return { matrix, daysEn, daysVn, tradingHours, maxVolumeInGrid };
  }, [chartData]);

  return (
    <div style={{ backgroundColor: activeColors.bg, color: activeColors.textMain, padding: '24px', borderRadius: '8px', border: `1px solid ${activeColors.border}`, maxWidth: '1200px', margin: '0 auto', fontFamily: 'monospace', boxSizing: 'border-box' }}>
      
      {/* TOOLBAR ĐIỀU KHIỂN CHỨC NĂNG TĨNH */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: '900', fontSize: '20px', letterSpacing: '0.5px' }}>
              VISUAL ANALYTICS: {activeSymbol || 'AAPL'}
            </div>
            <div style={{ fontSize: '11px', color: activeColors.textSub, fontWeight: 'bold', marginTop: '4px' }}>
              {isLoading ? '⏳ LOADING YAHOO ENGINE...' : '📷 ACCURATE TIMEZONE SNAPSHOT INFRASTRUCTURE'}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              value={inputSymbol}
              onChange={(e) => setInputSymbol(e.target.value)}
              placeholder="Ticker"
              onKeyDown={(e) => e.key === 'Enter' && handleFetchVisualData()}
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#fff', border: `1px solid ${activeColors.border}`, padding: '8px 12px', borderRadius: '4px', fontSize: '13px', fontWeight: 'bold', textTransform: 'uppercase', outline: 'none', width: '100px' }}
            />
            <button onClick={handleFetchVisualData} disabled={isLoading} style={{ backgroundColor: isLoading ? 'rgba(255,255,255,0.1)' : activeColors.green, color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: isLoading ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '13px' }}>
              {isLoading ? 'Loading...' : 'Fetch New'}
            </button>
          </div>
        </div>

        {/* TABS INTERVAL */}
        <div style={{ display: 'flex', gap: '4px', backgroundColor: 'rgba(255,255,255,0.02)', padding: '4px', borderRadius: '6px', border: `1px solid ${activeColors.border}` }}>
          {TIMEFRAMES.map(tf => {
            const isSelected = selectedTimeframe === tf.id;
            return (
              <button key={tf.id} onClick={() => setSelectedTimeframe(tf.id)} style={{ flex: 1, backgroundColor: isSelected ? 'rgba(255,255,255,0.12)' : 'transparent', color: isSelected ? '#fff' : activeColors.textSub, border: 'none', padding: '6px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                {tf.label}
              </button>
            );
          })}
        </div>
      </div>

      {errorMsg && <div style={{ color: activeColors.red, fontSize: '12px', padding: '12px', border: `1px solid ${activeColors.red}40`, borderRadius: '4px', marginBottom: '16px', backgroundColor: `${activeColors.red}05` }}>❌ Lỗi: {errorMsg}</div>}

      {/* BIỂU ĐỒ DIỆN RỘNG */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '24px', opacity: isLoading ? 0.3 : 1 }}>
        
        {/* CHART 1: LINE PRICE */}
        <div style={{ border: `1px solid ${activeColors.border}`, borderRadius: '6px', padding: '16px', backgroundColor: 'rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', color: activeColors.green, marginBottom: '12px' }}>1. LINE CHART: CLOSE PRICE TIMELINE ({currency})</div>
          <div style={{ width: '100%', height: '240px' }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.03)" />
                <XAxis dataKey="dateLabel" tick={{ fill: activeColors.textSub, fontSize: 10 }} stroke={activeColors.border} />
                <YAxis tick={{ fill: activeColors.textSub, fontSize: 10 }} stroke={activeColors.border} domain={['dataMin - 2', 'dataMax + 2']} />
                <Tooltip contentStyle={{ background: activeColors.bg, borderColor: activeColors.border, fontSize: 12, color: '#fff' }} />
                <Line type="monotone" dataKey="close" stroke={activeColors.green} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* CHART 2: VOLUME VERTICAL */}
        <div style={{ border: `1px solid ${activeColors.border}`, borderRadius: '6px', padding: '16px', backgroundColor: 'rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#3b82f6', marginBottom: '12px' }}>2. VOLUME BAR CHART: TRANSACTION INSTANT FLUX</div>
          <div style={{ width: '100%', height: '240px' }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.03)" />
                <XAxis dataKey="dateLabel" tick={{ fill: activeColors.textSub, fontSize: 10 }} stroke={activeColors.border} />
                <YAxis tick={{ fill: activeColors.textSub, fontSize: 9 }} stroke={activeColors.border} tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v} />
                <Tooltip contentStyle={{ background: activeColors.bg, borderColor: activeColors.border, fontSize: 12, color: '#fff' }} formatter={(v) => v.toLocaleString()} />
                <Bar dataKey="volume" fill="rgba(59, 130, 246, 0.75)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* CHART 3: VOLUME PROFILE (HORIZONTAL) */}
        <div style={{ border: `1px solid ${activeColors.border}`, borderRadius: '6px', padding: '16px', backgroundColor: 'rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#f472b6', marginBottom: '12px' }}>3. VOLUME PROFILE: VERTICAL PRICE ACCUMULATION</div>
          <div style={{ width: '100%', height: '260px' }}>
            <ResponsiveContainer>
              <BarChart data={volumeProfileData} layout="vertical" margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.03)" />
                <XAxis type="number" tick={{ fill: activeColors.textSub, fontSize: 9 }} stroke={activeColors.border} tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v} />
                <YAxis dataKey="priceLevel" type="category" tick={{ fill: activeColors.textMain, fontSize: 10 }} stroke={activeColors.border} />
                <Tooltip contentStyle={{ background: activeColors.bg, borderColor: activeColors.border, fontSize: 12, color: '#fff' }} formatter={(v) => v.toLocaleString()} />
                <Bar dataKey="volume" fill="rgba(244, 114, 182, 0.7)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* CHART 4: FIXED ACTIVATION HEATMAP (VÁ LỖI HOÀN TOÀN) */}
        <div style={{ border: `1px solid ${activeColors.border}`, borderRadius: '6px', padding: '16px', backgroundColor: 'rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#f59e0b', marginBottom: '14px' }}>4. HEATMAP MATRIX: EST TIMEZONE DAY VS HOUR VOLUME DENSITY</div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', overflowX: 'auto', paddingBottom: '8px' }}>
            {/* Hàng Tiêu đề trục Giờ */}
            <div style={{ display: 'grid', gridTemplateColumns: '75px repeat(10, 1fr)', gap: '4px', textAlign: 'center', fontSize: '9px', color: activeColors.textSub, fontWeight: 'bold' }}>
              <div></div>
              {heatmapMatrix.tradingHours.map(h => <div key={`h-th-${h}`}>{h}h</div>)}
            </div>

            {/* Quét nạp dữ liệu lưới ô cờ */}
            {heatmapMatrix.daysEn.map((dayEn, dIdx) => (
              <div key={`hm-row-${dayEn}`} style={{ display: 'grid', gridTemplateColumns: '75px repeat(10, 1fr)', gap: '4px', alignItems: 'center' }}>
                {/* Cột hiển thị tên Tiếng Việt */}
                <div style={{ fontSize: '11px', fontWeight: 'bold', color: activeColors.textMain }}>{heatmapMatrix.daysVn[dIdx]}</div>
                
                {heatmapMatrix.tradingHours.map(hour => {
                  const vol = heatmapMatrix.matrix[dayEn][hour];
                  const ratio = vol / heatmapMatrix.maxVolumeInGrid;
                  
                  // Xác định màu: Ô hoàn toàn trống lệnh sẽ có màu xám cực nhạt, ô có lệnh nhuộm màu cam hổ phách cuốn chiếu
                  const isZero = vol === 0;
                  const opacityAlpha = isZero ? 0.03 : Math.max(0.15, ratio);
                  const cellColor = isZero ? 'rgba(255,255,255,0.05)' : `rgba(245, 158, 11, ${opacityAlpha})`;

                  return (
                    <div 
                      key={`cell-${dayEn}-${hour}`}
                      title={`${heatmapMatrix.daysVn[dIdx]} lúc ${hour}:00 (Giờ Mỹ) | Khối lượng: ${vol.toLocaleString()}`}
                      style={{ 
                        height: '24px', 
                        backgroundColor: cellColor,
                        borderRadius: '2px',
                        border: (!isZero && vol === heatmapMatrix.maxVolumeInGrid) ? '1px solid #fff' : 'none',
                        cursor: isZero ? 'default' : 'pointer'
                      }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
          <div style={{ fontSize: '10px', color: activeColors.textSub, marginTop: '8px', lineHeight: '1.4' }}>
            * Lưới hiển thị chính xác theo trục giờ **Eastern Time (Giờ New York)**. Khung giờ từ **9h - 16h** là phiên chính khóa (Regular Session), ô màu cam càng đậm tương ứng volume tích tụ tại mốc giờ đó càng đột biến.
          </div>
        </div>

      </div>

    </div>
  );
}
